import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiZap, FiShield, FiCreditCard, FiLoader } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { PLAN_CONFIGS } from '@/config/planConfig';
import { supabase } from '@/integrations/supabase/client';
import { usePaymentSettings } from '@/hooks/usePaymentSettings';

// Calculate yearly price = monthly × 10 (pay 10 months, get 12 = ~17% off).
// Must match the Stripe price IDs in supabase/functions/create-checkout.
const calculateYearlyPrice = (monthlyPrice: number): number => {
  return monthlyPrice * 10;
};

const ChoosePlan: React.FC = () => {
  const navigate = useNavigate();
  const { profile, loading: profileLoading } = useSubscription();
  const { trialInfo, effectivePlan } = useFeatureFlags();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const paymentConfig = usePaymentSettings();

  // Redirect if user already has active subscription
  useEffect(() => {
    if (!profileLoading && profile) {
      const isActiveSubscription = 
        profile.billing_status === 'active' && 
        profile.subscription_plan_id && 
        !profile.is_in_trial;
      
      if (isActiveSubscription) {
        navigate('/dashboard');
      }
    }
  }, [profile, profileLoading, navigate]);

  const handleSelectPlan = async (planId: string) => {
    setError(null);
    setLoadingPlan(planId);

    // Free needs no Stripe checkout — the user already has the Free floor plan.
    if (planId === 'free') {
      setLoadingPlan(null);
      navigate('/dashboard');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke('create-checkout', {
        body: { plan_id: planId, billing_cycle: billingCycle },
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      if (data?.url) {
        // Open Stripe checkout in new tab
        window.open(data.url, '_blank');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout');
    } finally {
      setLoadingPlan(null);
    }
  };

  // Hide Free from the pricing grid (both monthly & yearly): Free is the silent
  // default plan assigned at signup by the DB (user_profiles.subscription_plan
  // DEFAULT 'free'). The grid is for UPGRADES — show only paid plans.
  const displayPlans = PLAN_CONFIGS.filter(p => !p.isEnterprise && p.id !== 'free');

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-950/20">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-400 text-sm mb-6">
            <FiZap className="w-4 h-4" />
            {trialInfo.inTrial 
              ? `${trialInfo.daysRemaining} days left in your trial` 
              : 'Your trial has ended'}
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Continue using Snap Chart AI with institutional-grade trading intelligence.
            Select the plan that fits your trading style.
          </p>
        </motion.div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto mb-8 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-center"
          >
            {error}
          </motion.div>
        )}

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-center items-center gap-4 mb-10"
        >
          <div className={`flex items-center gap-2 ${billingCycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
              <span className="text-cyan-400 text-xs font-bold">30</span>
            </div>
            <span className="text-lg font-semibold">Monthly</span>
          </div>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
            className="relative inline-flex h-8 w-16 items-center rounded-full bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 transition-transform ${
                billingCycle === 'yearly' ? 'translate-x-9' : 'translate-x-1'
              }`}
            />
          </button>
          <div className={`flex items-center gap-2 ${billingCycle === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
              <span className="text-cyan-400 text-xs font-bold">365</span>
            </div>
            <span className="text-lg font-semibold">Annual</span>
          </div>
          {billingCycle === 'yearly' && (
            <span className="ml-2 px-3 py-1 bg-green-500 text-white text-sm font-bold rounded-full">
              Save 20%
            </span>
          )}
        </motion.div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {displayPlans.map((plan, index) => {
            const isPopular = plan.id === 'pro_trader';
            const isLoading = loadingPlan === plan.id;
            const displayPrice = billingCycle === 'monthly' ? plan.priceMonthly : calculateYearlyPrice(plan.priceMonthly);
            
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className={`relative bg-card/50 backdrop-blur-sm border rounded-2xl p-8 ${
                  isPopular 
                    ? 'border-purple-500/50 ring-2 ring-purple-500/20 scale-105' 
                    : 'border-border/50 hover:border-purple-500/30'
                } transition-all duration-300`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1.5 bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm font-semibold rounded-full shadow-lg">
                      {plan.badge}
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-foreground mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    {plan.priceMonthly === 0 ? (
                      <span className="text-4xl font-bold text-foreground">Free</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-foreground">
                          ${displayPrice}
                        </span>
                        <span className="text-muted-foreground">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                      </>
                    )}
                  </div>
                  {plan.priceMonthly === 0 ? (
                    <p className="text-sm text-muted-foreground">No card required</p>
                  ) : billingCycle === 'yearly' ? (
                    <p className="text-sm text-green-400">${Math.round(displayPrice / 12)}/month • Save ${plan.priceMonthly * 12 - displayPrice}/year</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Billed monthly • Cancel anytime</p>
                  )}
                </div>

                {/* AI model badge — same treatment as AccountBilling
                    so users see the same "Powered by <model>" badge
                    everywhere plan cards render. */}
                {plan.aiModelLabel && (
                  <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-3 py-2 mb-6 font-mono text-[11px] leading-relaxed text-cyan-200">
                    <div className="uppercase tracking-widest opacity-60 mb-0.5">▸ Powered by</div>
                    <div className="text-foreground font-semibold">{plan.aiModelLabel}</div>
                    {plan.aiFallbackLabel && (
                      <div className="opacity-60 mt-0.5">↳ {plan.aiFallbackLabel}</div>
                    )}
                  </div>
                )}

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className={`p-1 rounded-full ${feature.included ? 'bg-green-500/20' : 'bg-muted/20'}`}>
                        <FiCheck className={`w-4 h-4 ${
                          feature.included ? 'text-green-400' : 'text-muted-foreground/30'
                        }`} />
                      </div>
                      <span className={feature.included ? 'text-foreground' : 'text-muted-foreground/50'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {(paymentConfig.stripeEnabled || plan.priceMonthly === 0) && (
                  <button
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={isLoading}
                    className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                      isPopular
                        ? 'bg-gradient-to-r from-purple-600 to-cyan-600 text-white hover:opacity-90 shadow-lg shadow-purple-500/25'
                        : 'bg-card border border-border text-foreground hover:bg-muted hover:border-purple-500/30'
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <FiLoader className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : plan.priceMonthly === 0 ? (
                      <>
                        <FiZap className="w-5 h-5" />
                        Continue Free
                      </>
                    ) : (
                      <>
                        <FiCreditCard className="w-5 h-5" />
                        Get Started
                      </>
                    )}
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Enterprise CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-4 p-6 bg-card/30 backdrop-blur-sm border border-border/50 rounded-2xl">
            <FiShield className="w-8 h-8 text-purple-400" />
            <div className="text-left">
              <h4 className="font-semibold text-foreground">Need Enterprise Features?</h4>
              <p className="text-sm text-muted-foreground">Custom pricing, compliance API, and white-label options</p>
            </div>
            <button
              onClick={() => navigate('/#contact')}
              className="px-6 py-2 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-xl hover:bg-purple-500/20 transition-colors"
            >
              Contact Sales
            </button>
          </div>
        </motion.div>

        {/* Security Badge */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <FiShield className="w-4 h-4" />
            Secure payment • 256-bit SSL encryption
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ChoosePlan;
